import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getClientAssignedCyclesAPI, // Corrected import
  getCycleEmployeesStatusAPI, 
  getMyAppraisalResponsesAPI, 
  getMyEmployeesAssignmentsAPI 
} from "api/apiHelper";
import { AvailableCycle, AppraisalResponse } from "types/appraisal-response-types";
import Card from "components/Card";
import Button from "components/Button";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import { toast } from "react-toastify";
import TabsContainer from "components/TabsContainer";
import Tab from "components/Tab";

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  photo?: string;
  email?: string;
  phone?: string;
  designation?: string;
}

interface CycleWithEmployees extends AvailableCycle {
  completedEmployees: Employee[];
  pendingEmployees: Employee[];
}

const Appraisals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cyclesWithEmployees, setCyclesWithEmployees] = useState<CycleWithEmployees[]>([]);
  const [completedResponses, setCompletedResponses] = useState<AppraisalResponse[]>([]);
  const [recurringAssignments, setRecurringAssignments] = useState<any[]>([]);

  useEffect(() => {
    fetchAvailableCycles();
    fetchCompletedResponses();
    fetchRecurringAssignments();
  }, []);

  const fetchRecurringAssignments = async () => {
    try {
      const response = await getMyEmployeesAssignmentsAPI(); 
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      const recurring = data.filter((a: any) => a.review_frequency !== 'NONE');
      setRecurringAssignments(recurring);
    } catch (err) {
      console.error("Failed to load recurring schedules", err);
    }
  };

  const fetchAvailableCycles = async () => {
    try {
      setLoading(true);
      setError(null);

      // Using the new API that only returns cycles where THIS client is active
      const cyclesResponse = await getClientAssignedCyclesAPI(); 
      const cycles = cyclesResponse.data;

      const cyclesWithStatus = await Promise.all(
        cycles.map(async (cycle: any) => {
          try {
            const statusResponse = await getCycleEmployeesStatusAPI(cycle.id);
            return {
              ...cycle,
              completedEmployees: statusResponse.data.completed || [],
              pendingEmployees: statusResponse.data.pending || []
            };
          } catch (err) {
            return { ...cycle, completedEmployees: [], pendingEmployees: [] };
          }
        })
      );

      // Typed 'c' as 'any' to resolve the TS7006 error
      setCyclesWithEmployees(cyclesWithStatus.filter((c: any) => c.pendingEmployees.length > 0));
    } catch (err: any) {
      setError("Failed to load your active appraisal cycles.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedResponses = async () => {
    try {
      const response = await getMyAppraisalResponsesAPI('SUBMITTED');
      setCompletedResponses(response.data);
    } catch (err: any) {
      console.error("Failed to load completed responses:", err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isDeadlinePassed = (deadline: string) => {
    return new Date(deadline) < new Date();
  };

  if (loading && cyclesWithEmployees.length === 0) {
    return <Backdrop show={true} />;
  }

  return (
    <div className="mt-5">
      {error && <ErrorBox errorMessage={error} />}

      <TabsContainer defaultTab={0}>
        <Tab isActive={false} title="Available Appraisals">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Available Appraisals
            </h2>

            {cyclesWithEmployees.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                <i className="fas fa-check-circle text-4xl text-green-500 mb-3 block"></i>
                You have completed all available appraisals!
              </div>
            ) : (
              <div className="space-y-4">
                {cyclesWithEmployees.map((cycle) => (
                  <div
                    key={cycle.id}
                    className="border border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                          {cycle.name}
                        </h3>
                        {cycle.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {cycle.description}
                          </p>
                        )}
                      </div>
                      {isDeadlinePassed(cycle.deadline) && (
                        <span className="px-2 py-1 text-xs font-semibold text-red-600 bg-red-100 dark:bg-red-900/20 rounded">
                          Deadline Passed
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Deadline: </span>
                        <span className="text-navy-700 dark:text-white font-medium">
                          {formatDate(cycle.deadline)}
                        </span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm font-semibold text-brand-500 mb-3 uppercase tracking-wider">
                        Pending Reviews ({cycle.pendingEmployees.length}):
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {cycle.pendingEmployees.map((employee) => (
                          <div
                            key={employee.id}
                            onClick={() => !isDeadlinePassed(cycle.deadline) && navigate(`/client-appraisals/${cycle.id}/employee/${employee.id}`)}
                            className={`bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer ${
                              isDeadlinePassed(cycle.deadline) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {employee.photo ? (
                                <img
                                  src={employee.photo}
                                  alt={employee.full_name}
                                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center font-bold text-gray-600 dark:text-gray-400 text-sm border-2 border-gray-200 dark:border-navy-600">
                                  {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-navy-700 dark:text-white truncate">
                                  {employee.full_name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {employee.designation || 'Team Member'}
                                </p>
                              </div>
                              <i className="fas fa-chevron-right text-gray-500 dark:text-gray-400 text-xs"></i>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Tab>

        <Tab isActive={false} title="Completed Appraisals">
          <Card extra="p-4">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
              Completed Appraisals
            </h2>

            {completedResponses.length === 0 ? (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                No completed appraisals yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedResponses.map((response) => (
                  <div
                    key={response.id}
                    className="bg-white dark:bg-navy-800 border-2 border-gray-200 dark:border-navy-700 rounded-lg p-5 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/client-appraisals/response/${response.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-navy-700 dark:text-white mb-2 truncate">
                          {response.cycle.name}
                        </h3>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600 text-gray-600 dark:text-gray-400 font-bold text-xs">
                            {(response.employee?.first_name?.[0] || '') + (response.employee?.last_name?.[0] || '')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-navy-700 dark:text-white truncate text-sm">
                              {response.employee?.full_name}
                            </p>
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">
                              Verified Review
                            </p>
                          </div>
                        </div>
                      </div>
                      <span className="px-2 py-1 text-[10px] font-bold text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-navy-600 rounded-full flex-shrink-0">
                        SUBMITTED
                      </span>
                    </div>

                    <div className="pt-3 border-t border-gray-100 dark:border-navy-700 flex items-center justify-between text-[11px] text-gray-500">
                      <div className="flex items-center gap-1">
                        <i className="fas fa-calendar-check"></i>
                        {response.submitted_at ? formatDate(response.submitted_at) : 'N/A'}
                      </div>
                      <span className="text-brand-500 font-bold">View Results →</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Tab>
      </TabsContainer>
    </div>
  );
};

export default Appraisals;